import { useEffect, useState } from 'react';
import { Alert, App, Form, Input, Modal, Select } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, NetworkError } from '../../api/client';
import type { Branch } from '../catalog/api';
import { isSuperAdmin, useCurrentStaff } from '../../auth/session';
import {
  ROLE_LABELS,
  staffApi,
  staffKeys,
  type CreateStaffResult,
  type StaffRoleEnum,
  type StaffStatus,
  type StaffUser,
} from './api';

interface FormValues {
  username: string;
  name: string;
  phone?: string;
  role: StaffRoleEnum;
  branchId?: string;
  status: StaffStatus;
}

const STATUS_OPTIONS: { value: StaffStatus; label: string }[] = [
  { value: 'Active', label: 'Đang làm việc' },
  { value: 'Disabled', label: 'Đã khóa' },
];

interface Props {
  staff: StaffUser | null;
  isCreating: boolean;
  branches: Branch[];
  onCreated: (result: CreateStaffResult) => void;
  onClose: () => void;
}

export function StaffFormModal({ staff, isCreating, branches, onCreated, onClose }: Props) {
  const [form] = Form.useForm<FormValues>();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const currentStaff = useCurrentStaff();

  const isEditing = staff !== null;
  const open = isEditing || isCreating;

  const [selectedRole, setSelectedRole] = useState<StaffRoleEnum>('Cashier');

  // Chỉ super_admin mới tạo được super_admin khác. Quản lý chi nhánh tạo được
  // thu ngân và lễ tân cho chi nhánh mình — server mới là nơi chốt (BR-801).
  const roleOptions = (
    isSuperAdmin(currentStaff)
      ? (['SuperAdmin', 'BranchManager', 'Cashier', 'Host'] as StaffRoleEnum[])
      : (['Cashier', 'Host'] as StaffRoleEnum[])
  ).map((role) => ({ value: role, label: ROLE_LABELS[role] }));

  useEffect(() => {
    if (!open) return;

    if (staff !== null) {
      setSelectedRole(staff.role);
      form.setFieldsValue({
        username: staff.username,
        name: staff.name,
        phone: staff.phone ?? undefined,
        role: staff.role,
        branchId: staff.branchId ?? undefined,
        status: staff.status,
      });
    } else {
      form.resetFields();
      const defaultRole: StaffRoleEnum = 'Cashier';
      setSelectedRole(defaultRole);
      form.setFieldsValue({
        role: defaultRole,
        status: 'Active',
        // Quản lý chi nhánh chỉ tạo được người cho chính chi nhánh mình
        branchId: currentStaff?.branchId ?? undefined,
      });
    }
  }, [open, staff, form, currentStaff]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const phone = values.phone?.trim() === '' ? null : (values.phone ?? null);

      if (staff !== null) {
        // Vai trò và chi nhánh KHÔNG sửa được sau khi tạo: đổi vai trò tại chỗ sẽ
        // làm các bút toán cũ trong audit_logs mất ngữ cảnh ai được phép làm gì
        await staffApi.update(staff.id, { name: values.name, phone, status: values.status });
        return null;
      }

      return staffApi.create({
        username: values.username,
        name: values.name,
        phone,
        role: values.role,
        branchId: values.role === 'SuperAdmin' ? null : (values.branchId ?? null),
      });
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: staffKeys.all });

      if (result === null) {
        message.success('Đã lưu thông tin nhân viên.');
        onClose();
        return;
      }

      onCreated(result);
    },
  });

  const errorText =
    mutation.error instanceof ApiError || mutation.error instanceof NetworkError
      ? mutation.error.message
      : mutation.isError
        ? 'Có lỗi không xác định. Thử lại hoặc báo kỹ thuật.'
        : null;

  return (
    <Modal
      open={open}
      title={isEditing ? `Sửa ${staff.name}` : 'Thêm nhân viên'}
      okText={isEditing ? 'Lưu thay đổi' : 'Tạo tài khoản'}
      cancelText="Hủy"
      confirmLoading={mutation.isPending}
      onCancel={() => {
        mutation.reset();
        onClose();
      }}
      onOk={() => void form.submit()}
      destroyOnHidden
    >
      {errorText !== null && (
        <Alert style={{ marginBottom: 16 }} type="error" showIcon message={errorText} />
      )}

      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => mutation.mutate(values)}
      >
        <Form.Item
          label="Tên đăng nhập"
          name="username"
          tooltip={isEditing ? undefined : 'Không đổi được sau khi tạo.'}
          rules={[
            { required: true, message: 'Chưa nhập tên đăng nhập.' },
            {
              pattern: /^[a-z0-9._-]+$/,
              message: 'Tên đăng nhập chỉ gồm chữ thường, số và các ký tự . _ -',
            },
          ]}
        >
          <Input maxLength={50} disabled={isEditing} autoFocus={!isEditing} />
        </Form.Item>

        <Form.Item
          label="Họ và tên"
          name="name"
          rules={[{ required: true, message: 'Chưa nhập họ tên.' }]}
        >
          <Input maxLength={200} autoFocus={isEditing} />
        </Form.Item>

        <Form.Item label="Số điện thoại" name="phone">
          <Input maxLength={20} inputMode="tel" />
        </Form.Item>

        <Form.Item
          label="Vai trò"
          name="role"
          tooltip={isEditing ? 'Vai trò không đổi được. Cần đổi thì khóa tài khoản và tạo tài khoản mới.' : undefined}
        >
          <Select
            options={roleOptions}
            disabled={isEditing}
            onChange={(value: StaffRoleEnum) => setSelectedRole(value)}
          />
        </Form.Item>

        {selectedRole !== 'SuperAdmin' && (
          <Form.Item
            label="Chi nhánh"
            name="branchId"
            tooltip={isEditing ? 'Chi nhánh không đổi được sau khi tạo.' : undefined}
            rules={[{ required: true, message: 'Chưa chọn chi nhánh.' }]}
          >
            <Select
              disabled={isEditing || !isSuperAdmin(currentStaff)}
              showSearch
              optionFilterProp="label"
              options={branches.map((branch) => ({
                value: branch.id,
                label: `${branch.chainName} — ${branch.name}`,
              }))}
            />
          </Form.Item>
        )}

        {isEditing && (
          <Form.Item
            label="Trạng thái"
            name="status"
            tooltip="Khóa tài khoản sẽ cắt mọi phiên đang mở của người này."
          >
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
